import { useState, useEffect, useCallback, useRef } from 'react';
import { StorageService } from '../services/storage';
import type { StorageResult } from '../services/storage';

export interface AuthStatus {
  isAuthenticated: boolean;
  error: 'auth' | 'network' | 'server' | 'unknown' | null;
  message: string | null;
  lastCheck: number | null;
}

const HEALTH_CHECK_INTERVAL = 45000; // 45 seconds
const MIN_TIME_BETWEEN_CHECKS = 5000; // Don't check more than once per 5 seconds

/**
 * Hook to monitor authentication status for cloud storage
 * Detects when Cloudflare Zero Trust authentication is missing or expired
 */
export function useAuthStatus() {
  const [status, setStatus] = useState<AuthStatus>({
    isAuthenticated: true, // Optimistically assume authenticated
    error: null,
    message: null,
    lastCheck: null,
  });

  const lastCheckRef = useRef<number>(0);
  const healthCheckIntervalRef = useRef<number | null>(null);

  // Detect if app is running in standalone/PWA mode
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );

  // Perform a health check by attempting a lightweight API call
  const performHealthCheck = useCallback(async (): Promise<StorageResult> => {
    const now = Date.now();
    // Throttle checks to avoid excessive API calls
    if (now - lastCheckRef.current < MIN_TIME_BETWEEN_CHECKS) {
      return { success: true }; // Skip if too soon
    }
    lastCheckRef.current = now;

    try {
      // Try to fetch a small amount of data to test auth
      // Using getQuotes as it's a common operation
      const quotes = await StorageService.getQuotes();
      
      // If we got here without an auth error event, we're authenticated
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: 'network',
        message: error instanceof Error ? error.message : 'Health check failed'
      };
    }
  }, []);

  // Handle storage auth failure events
  useEffect(() => {
    const handleAuthFailure = (event: CustomEvent<StorageResult>) => {
      const result = event.detail;
      if (result.error === 'auth') {
        setStatus({
          isAuthenticated: false,
          error: 'auth',
          message: result.message || 'Authentication required',
          lastCheck: Date.now(),
        });
      }
    };

    window.addEventListener('storage-auth-failure', handleAuthFailure as EventListener);
    return () => {
      window.removeEventListener('storage-auth-failure', handleAuthFailure as EventListener);
    };
  }, []);

  // Handle successful saves (indicates auth is working)
  useEffect(() => {
    const handleStorageSuccess = () => {
      // If we were previously unauthenticated, mark as authenticated
      setStatus(prev => {
        if (!prev.isAuthenticated) {
          return {
            isAuthenticated: true,
            error: null,
            message: null,
            lastCheck: Date.now(),
          };
        }
        return prev;
      });
    };

    // Monitor for successful API calls (we'll dispatch this from storage service)
    window.addEventListener('storage-success', handleStorageSuccess);
    return () => {
      window.removeEventListener('storage-success', handleStorageSuccess);
    };
  }, []);

  // Periodic health checks
  useEffect(() => {
    // Initial health check after a short delay
    const initialTimeout = setTimeout(() => {
      performHealthCheck().then(result => {
        if (!result.success && result.error === 'auth') {
          setStatus({
            isAuthenticated: false,
            error: 'auth',
            message: result.message || 'Authentication required',
            lastCheck: Date.now(),
          });
        }
      });
    }, 2000);

    // Set up periodic health checks
    healthCheckIntervalRef.current = window.setInterval(() => {
      performHealthCheck().then(result => {
        if (!result.success && result.error === 'auth') {
          setStatus({
            isAuthenticated: false,
            error: 'auth',
            message: result.message || 'Authentication required',
            lastCheck: Date.now(),
          });
        } else if (result.success) {
          // If health check succeeds, we're authenticated
          setStatus(prev => ({
            ...prev,
            isAuthenticated: true,
            error: null,
            message: null,
            lastCheck: Date.now(),
          }));
        }
      });
    }, HEALTH_CHECK_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      if (healthCheckIntervalRef.current !== null) {
        clearInterval(healthCheckIntervalRef.current);
      }
    };
  }, [performHealthCheck]);

  // Check auth on visibility change (when user switches back to app)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // App regained focus, check auth status
        performHealthCheck().then(result => {
          if (!result.success && result.error === 'auth') {
            setStatus({
              isAuthenticated: false,
              error: 'auth',
              message: result.message || 'Authentication required',
              lastCheck: Date.now(),
            });
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [performHealthCheck]);

  // Force a health check (can be called manually)
  const checkAuth = useCallback(async () => {
    const result = await performHealthCheck();
    if (!result.success && result.error === 'auth') {
      setStatus({
        isAuthenticated: false,
        error: 'auth',
        message: result.message || 'Authentication required',
        lastCheck: Date.now(),
      });
    } else if (result.success) {
      setStatus({
        isAuthenticated: true,
        error: null,
        message: null,
        lastCheck: Date.now(),
      });
    }
    return result;
  }, [performHealthCheck]);

  // Trigger authentication (reload page to trigger Cloudflare Access)
  const triggerAuth = useCallback(() => {
    window.location.reload();
  }, []);

  return {
    ...status,
    isStandalone,
    checkAuth,
    triggerAuth,
  };
}

